import { Navigate, Route, Routes } from 'react-router-dom';
import { useAppContext, useDrawerContext } from 'context/hooks';
import { useState } from 'react';
import { PatientsTable } from 'pages/Main/Patients/PatientsTable';
import { Shell } from 'layout/Shell/Shell';
import { Profile } from 'pages/Profile/Profile';
import { GroupSettingsTable } from 'pages/Settings/GroupSettingsTable';
import { GroupSettings } from 'pages/Settings/GroupSettings';
import { PatientDetails } from 'pages/Main/Patients/Details/PatientDetails';
import { ReportsTable } from 'pages/Main/Reports/ReportsTable';
import { LoadingOverlay } from '@mantine/core';
import { WorklistTable } from 'pages/Main/Worklist/WorklistTable';
import { Feedback } from 'pages/Main/Feedback/Feedback';
import { NoStaffModal } from 'components/Modal/NoStaffModal';
import { sdk } from 'utils/query';
import {
  IDFormatError,
  NoPermissionError,
  OauthTokenError,
  UserNotAuthenticatedError,
} from '@extrahorizon/javascript-sdk';
import { RoutePath } from 'utils/location';
import { ApproveDocumentsModal } from 'components/Modal/ApproveDocumentsModal';
import { NetworkErrorModal } from 'components/Modal/NetworkErrorModal';
import { NetworkError } from 'services/errors/NetworkError';
import { SessionExpiredModal } from 'components/Modal/SessionExpiredModal';
import { IdNotFoundModal } from 'components/Modal/IdNotFoundModal';
import { Dashboard } from 'pages/Main/Dashboard/Dashboard';
import { showUsaFeature, showWorklistFeature } from 'utils/app';
import { AxiosError } from 'axios';
import { Billing } from 'pages/Main/Billing/Billing';
import { About } from 'pages/Main/About/About';
import { ElementNotFoundError } from './services/errors/ElementNotFoundError';

export const Router = () => {
  const { currentUser, isLoading } = useAppContext();
  const [errorToCheck, setError] = useState<Error>();
  const { closeDrawer } = useDrawerContext();

  const createSdkInterceptors = () => {
    sdk.raw.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.code === 'ERR_NETWORK') {
          closeDrawer();
          throw new NetworkError();
        }
        setError(error);
        return Promise.reject(error);
      },
    );
  };

  createSdkInterceptors();

  if (isLoading) {
    return <LoadingOverlay visible={true} />;
  }
  if (errorToCheck instanceof NetworkError || errorToCheck instanceof AxiosError) {
    return <NetworkErrorModal />;
  }
  if (errorToCheck instanceof UserNotAuthenticatedError || errorToCheck instanceof OauthTokenError) {
    return <SessionExpiredModal />;
  }
  if (
    errorToCheck instanceof NoPermissionError ||
    errorToCheck instanceof ElementNotFoundError ||
    errorToCheck instanceof IDFormatError
  ) {
    return <IdNotFoundModal setError={setError} />;
  }

  if (!currentUser) {
    return <NoStaffModal />;
  }

  const firstDocumentToApprove = currentUser.documentsToApprove.find((document) => document.hasApproved === false);
  if (firstDocumentToApprove) {
    return <ApproveDocumentsModal document={firstDocumentToApprove} userId={currentUser.user.id} />;
  }

  return (
    <Routes>
      <Route path={'/'} element={<Shell />}>
        <Route path={RoutePath.Dashboard} element={<Dashboard />} />
        {showWorklistFeature && <Route path={RoutePath.Worklist} element={<WorklistTable />} />}
        <Route path={RoutePath.Patients} element={<PatientsTable />} />
        <Route path={RoutePath.PatientDetails} element={<PatientDetails />} />
        <Route path={RoutePath.Reports} element={<ReportsTable />} />
        {showUsaFeature && <Route path={RoutePath.Billing} element={<Billing />} />}
        <Route path={RoutePath.Settings} element={<GroupSettingsTable />} />
        <Route path={RoutePath.SettingsGroup} element={<GroupSettings />} />
        <Route path={RoutePath.Profile} element={<Profile />} />
        <Route path={RoutePath.Feedback} element={<Feedback />} />
        <Route path={RoutePath.About} element={<About />} />
        <Route path={'*'} element={<Navigate to={RoutePath.Dashboard} replace />} />
        <Route path={''} element={<Navigate to={RoutePath.Dashboard} replace />} />
      </Route>
    </Routes>
  );
};
